import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbActiveModal, NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { Subject, takeUntil } from 'rxjs';
import { ErrorHandlerService } from '../../../../../../_shared/services/error-handler.service';
import { MaintenanceOverviewService } from '../../../services/maintenance-overview.service';
import { EmptyStateComponent } from '../../../../../../_shared/components/states/empty-state/empty-state.component';
import { GraphTrendIconComponent } from '../../../../../../_shared/components/graphs/graph-trend-icon/graph-trend-icon.component';
import { AccountFilterComponent } from '../../../../../../_shared/components/filters/account-filter/account-filter.component';
import { DateFilterComponent } from '../../../../../../_shared/components/filters/date-filter/date-filter.component';
import { HorizontalBarChartComponent, HorizontalBarChartData, HorizontalBarChartReferenceLine } from '../../../../../../_shared/components/graphs/horizontal-bar-chart/horizontal-bar-chart.component';
import { AccountFilterData, DateIntervalFilterData } from '../../../../../../_shared/models/filters/widget-filter-data.model';
import { AccountDateFilterRequest } from '../../../../../../_shared/models/accountDateFilter.model';
import { isEmptyObject } from '../../../../../../_shared/helpers/object.helper';
import { DateInterval } from '../../../../../../_shared/enums';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ReactiveMaintenanceResponse } from '../../../models/maintenance-overview-response.model';

@Component({
  selector: 'app-reactive-maintenance-graph-modal',
  standalone: true,
  imports: [
    CommonModule,
    NgbDropdownModule,
    EmptyStateComponent,
    GraphTrendIconComponent,
    AccountFilterComponent,
    DateFilterComponent,
    HorizontalBarChartComponent,
    TranslocoDirective,
    TranslocoPipe
  ],
  templateUrl: './reactive-maintenance-graph-modal.component.html',
  styleUrl: './reactive-maintenance-graph-modal.component.scss'
})
export class ReactiveMaintenanceGraphModalComponent implements OnInit, OnDestroy {
  private activeModal = inject(NgbActiveModal);
  private maintenanceService = inject(MaintenanceOverviewService);
  private errorHandlerService = inject(ErrorHandlerService);
  private transloco = inject(TranslocoService);

  chartData: HorizontalBarChartData[] = [];
  /** Raw API rows; `chartData` is rebuilt from these so the bar labels follow a language switch. */
  private reactiveMaintenanceData: ReactiveMaintenanceResponse[] = [];
  isLoading: boolean = true;
  isExporting: boolean = false;
  private readonly chartColors = ['#E46F2C', '#8D63C3', '#7F7FD7', '#DCC2E3', '#F4BC2B'];

  title: string = this.transloco.translate('maintenanceOverview.modals.reactiveGraph.title');

  accountFilter: AccountFilterData;
  dateFilter: DateIntervalFilterData;

  defaultInterval: DateInterval = DateInterval.Month;
  disabledIntervals: DateInterval[] = [DateInterval.Half];

  totalValue: number = 0;
  percentageChange: number = 0;

  private cancelSubject: Subject<void> = new Subject<void>();

  // Chart configuration
  referenceLines: HorizontalBarChartReferenceLine[] = [
    { value: 75, color: '#3873C9', label: this.transloco.translate('maintenanceOverview.modals.reactiveGraph.average') },
    { value: 85, color: '#E74C3C', label: this.transloco.translate('maintenanceOverview.modals.reactiveGraph.target') }
  ];

  constructor() {
    this.accountFilter = {} as AccountFilterData;
    this.dateFilter = {} as DateIntervalFilterData;
  }

  async ngOnInit(): Promise<void> {
    this.transloco.selectTranslation()
      .pipe(takeUntil(this.cancelSubject))
      .subscribe(() => this.buildChartData());

    if (!isEmptyObject(this.dateFilter) && !isEmptyObject(this.accountFilter)) {
      await this.loadData();
    }
  }

  ngOnDestroy(): void {
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  async onAccountFilterChange(filter: AccountFilterData): Promise<void> {
    this.accountFilter = filter;
    this.updateFilter();
  }

  async onDateFilterChange(filter: DateIntervalFilterData): Promise<void> {
    this.dateFilter = filter;
    this.updateFilter();
  }

  updateFilter(): void {
    if (!isEmptyObject(this.dateFilter) && !isEmptyObject(this.accountFilter)) {
      this.loadData();
    }
  }

  async loadData(): Promise<void> {
    this.isLoading = true;

    try {
      const request: AccountDateFilterRequest = {
        accountIds: this.accountFilter.accountIds,
        timeFrom: this.dateFilter.startDate!,
        timeTo: this.dateFilter.endDate!
      };

      const [summaryResponse, reactiveMaintenanceResponse] = await Promise.all([
        this.maintenanceService.getReactiveMaintenanceSummary({
          ...request,
          previousTimeFrom: this.dateFilter.previousCycleStartDate!,
          previousTimeTo: this.dateFilter.previousCycleEndDate!
        }, this.cancelSubject),
        this.maintenanceService.getReactiveMaintenance(request, this.cancelSubject)
      ]);

      this.totalValue = summaryResponse.data?.totalValue ?? 0;
      this.percentageChange = summaryResponse.data?.percentageChange ?? 0;

      const data = reactiveMaintenanceResponse.data;
      if (data) {
        this.reactiveMaintenanceData = data;
        this.buildChartData();
      }

      this.isLoading = false;
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.isLoading = false;
          this.reactiveMaintenanceData = [];
          this.chartData = [];
        }
      });
    }
  }

  /**
   * The horizontal bar chart draws its axis labels and tooltip straight from `name`, so `name` carries the
   * reason in the user's language (display only; `reactiveMaintenanceData` keeps the raw backend name).
   */
  private buildChartData(): void {
    this.chartData = this.reactiveMaintenanceData
      .map(item => ({
        name: item.localizedMaintenanceReason || item.maintenanceReason,
        value: item.maintenanceCount,
        color: ''
      }))
      .sort((a, b) => b.value - a.value)
      .map((item, index) => ({
        ...item,
        color: this.chartColors[index % this.chartColors.length]
      }));
  }

  formatPercentChange(): string {
    const sign = this.percentageChange > 0 ? '+' : '';
    return `${sign}${this.percentageChange.toLocaleString('default', { maximumFractionDigits: 2 })}%`;
  }

  isUptrendPositive(): boolean {
    return this.percentageChange < 0;
  }

  formatValue(): string {
    return `${this.totalValue.toLocaleString('default', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
  }

  hasData(): boolean {
    return this.chartData && this.chartData.length > 0 && this.chartData.some(item => item.value > 0);
  }

  get legendItems(): HorizontalBarChartData[] {
    return this.chartData;
  }

  close(): void {
    this.activeModal.close();
  }

  onExportExcel(): void {
    console.log('Export as Excel clicked');
    // TODO: Implement Excel export
  }

  onExportCsv(): void {
    console.log('Export as CSV clicked');
    // TODO: Implement CSV export
  }
}